import { useMemo, useState } from 'react';
import { useGetList, useGetMany } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { colorChoices } from '@/constants/TagCategoryColorChoices';
import { createFilterToQuery } from '@/helpers/filters/filterToQuery.js';

function groupTagsByCategory(tags) {
    if (!Array.isArray(tags)) return [];
    return tags.reduce((acc, tag) => {
        const category = tag.category || {};
        const categoryId = category.id ?? tag.categoryId;
        const existingCategory = acc.find(cat => cat.id === categoryId);
        const colorChoice = colorChoices.find(choice => choice.id === category.color);

        if (existingCategory) {
            existingCategory.tags.push(tag);
            return acc;
        }

        acc.push({
            id: categoryId,
            name: category.name,
            nameEn: category.nameEn,
            color: {
                light: colorChoice?.shades?.[50] || category.color,
                dark: colorChoice?.shades?.[200] || category.color,
            },
            createdAt: category.createdAt,
            updatedAt: category.updatedAt,
            tags: [tag],
        });
        return acc;
    }, []);
}

export function useTagPicker({ searchFields, baseFilter = {}, forceDesktopOnMobile = false, selectedIds = [] } = {}) {
    const [searchText, setSearchText] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const mobileMode = isMobile && !forceDesktopOnMobile;

    const toQuery = createFilterToQuery(searchFields);
    const filter = { ...baseFilter, ...(searchText ? toQuery(searchText) : {}) };
    const { data: tags, isLoading } = useGetList('tag', { filter, pagination: { perPage: 250 } });

    const { data: selectedTags } = useGetMany('tag', { ids: selectedIds.length > 0 ? selectedIds : [] });

    const allTags = useMemo(() => {
        if (!selectedIds.length || !selectedTags || selectedTags.length === 0) return tags;
        const tagIds = new Set((tags || []).map(t => t.id));
        const uniqueSelectedTags = (selectedTags || []).filter(st => !tagIds.has(st.id));
        return [...(tags || []), ...uniqueSelectedTags];
    }, [tags, selectedTags, selectedIds.length]);

    const groupedTags = useMemo(() => groupTagsByCategory(allTags), [allTags]);

    const filterToQuery = text => {
        setSearchText(text);
        return text ? toQuery(text) : {};
    };

    const toggleAllSelection = currentTempSelectedIds => {
        const allTagIds = (groupedTags || []).flatMap(category => (category.tags || []).map(tag => tag.id));
        const noneSelected = (currentTempSelectedIds || []).length === 0;
        return noneSelected ? allTagIds : [];
    };

    return {
        searchText,
        setSearchText,
        filterToQuery,
        tags: allTags,
        groupedTags,
        isLoading,
        mobileMode,
        toggleAllSelection,
    };
}

export default useTagPicker;
